import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { formatDate, useEffectOnce, usePathParam } from '@chilecompra/react-kit'
import { Card, Skeleton, Table, TableRow, Toast, Typography } from '@chilecompra/react-kit/components'
import { checkIfForeignDni } from '../../modules/utils/helpers'
import { onGetLegalSuitabilityThunk, onGetStartDateActivityThunk } from './LegalSuitabilitySection.actions'
import { Column, Content, Row, TableCell } from './LegalSuitabilitySection.styles'
import ProtectByFeatures from '../../components/Security/ProtectByFeatures'
import { REACT_APP_FEATURE_FLAG_BUSINESS_ACTIVITIES_START_DATE } from '../../config/settings/environment'

/**
 * The Legal Suitability section container.
 */
const LegalSuitabilitySection = () => {
  const dispatch = useDispatch()
  const dni = usePathParam('rut')
  const isForeign = checkIfForeignDni(dni)
  const { sellerType } = useSelector(state => state.basicSellerInformation)
  const { creationDate, error, loading, loadingStartDate, societyType, source, startDateActivity } = useSelector(
    state => state.legalSuitability
  )

  useEffect(() => {
    if (!loading) {
      return
    }

    if (sellerType === 'legal' && !isForeign) {
      dispatch(onGetLegalSuitabilityThunk({ dni }))
    }
  }, [sellerType])

  useEffectOnce(() => {
    if (!loadingStartDate) {
      return
    }

    if (!isForeign) {
      dispatch(onGetStartDateActivityThunk({ dni }))
    }
  })

  return error?.reason === 'FORBIDDEN_ERROR' ? null : (
    <Content>
      <Typography fontWeight="bold" padding="0 0 16px" variant="h4">
        Idoneidad del proveedor
      </Typography>
      <Typography variant="body2">
        Resumen de la situación legal, financiera y técnica del proveedor traída desde fuentes oficiales.
      </Typography>

      <Card color="blank" enableBorder margin="24px 0 0" padding="32px 24px 8px">
        <Row>
          <Column width="200px">
            <Typography fontWeight="bold" padding="0 0 3px" variant="h4">
              Situación legal
            </Typography>
            <Typography variant="subtitle">
              {`Fuente: ${source === 'SII' ? 'Servicio de Impuestos Internos' : 'E1D'} `}
            </Typography>
          </Column>

          {!error && !isForeign && (
            <Column flexBasis="60%" flexGrow={2}>
              <Table accessibility={{ label: 'Información del proveedor' }}>
                <TableRow>
                  <TableCell
                    disableBottomBorder={
                      sellerType === '' ||
                      REACT_APP_FEATURE_FLAG_BUSINESS_ACTIVITIES_START_DATE === 'false' ||
                      REACT_APP_FEATURE_FLAG_BUSINESS_ACTIVITIES_START_DATE === false
                    }
                    enableHead
                    padding={
                      sellerType === '' ||
                      REACT_APP_FEATURE_FLAG_BUSINESS_ACTIVITIES_START_DATE === 'false' ||
                      REACT_APP_FEATURE_FLAG_BUSINESS_ACTIVITIES_START_DATE === false
                        ? '8px 0 0 0'
                        : '0 8px 8px 0'
                    }
                    width="150px"
                  >
                    <Typography fontWeight="bold" variant="body2">
                      {loading ? (
                        <Skeleton width="100%" />
                      ) : societyType === 'PERSONA NATURAL' ? (
                        'Tipo de empresa'
                      ) : (
                        'Tipo de sociedad'
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell
                    disableBottomBorder={
                      sellerType === '' ||
                      REACT_APP_FEATURE_FLAG_BUSINESS_ACTIVITIES_START_DATE === 'false' ||
                      REACT_APP_FEATURE_FLAG_BUSINESS_ACTIVITIES_START_DATE === false
                    }
                    padding={
                      sellerType === '' ||
                      REACT_APP_FEATURE_FLAG_BUSINESS_ACTIVITIES_START_DATE === 'false' ||
                      REACT_APP_FEATURE_FLAG_BUSINESS_ACTIVITIES_START_DATE === false
                        ? '8px 0 0 0'
                        : '0 8px 8px 0'
                    }
                  >
                    <Typography variant="body2">{loading ? <Skeleton width="100%" /> : societyType}</Typography>
                  </TableCell>
                </TableRow>
                <ProtectByFeatures rule={features => features['com.seller/activity.start.date']}>
                  <TableRow>
                    <TableCell disableBottomBorder={sellerType !== 'legal'}>
                      <Typography fontWeight="bold" variant="body2">
                        {loading ? (
                          <Skeleton width="100%" />
                        ) : sellerType === 'natural' ? (
                          'Fecha de inicio de actividades'
                        ) : (
                          'Fecha de constitución de la sociedad'
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell disableBottomBorder={sellerType !== 'legal'}>
                      <Typography variant="body2">
                        {sellerType !== 'natural' && (
                          <Typography variant="body2">
                            {loading ? <Skeleton width="100%" /> : formatDate(creationDate, 'dd MMMM yyyy')}
                          </Typography>
                        )}
                        {sellerType === 'natural' && (
                          <Typography variant="body2">
                            {loadingStartDate ? (
                              <Skeleton width="100%" />
                            ) : (
                              formatDate(startDateActivity, 'dd MMMM yyyy')
                            )}
                          </Typography>
                        )}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </ProtectByFeatures>
                <ProtectByFeatures rule={features => features['com.seller/activity.start.date']}>
                  {loadingStartDate && <Skeleton width="100%" />}
                  {!loadingStartDate && societyType !== 'PERSONA NATURAL' && (
                    <TableRow>
                      <TableCell disableBottomBorder>
                        <Typography fontWeight="bold" variant="body2">
                          {loadingStartDate ? <Skeleton width="100%" /> : 'Inicio de actividades'}
                        </Typography>
                      </TableCell>
                      <TableCell disableBottomBorder>
                        <Typography variant="body2">
                          {loadingStartDate ? <Skeleton width="100%" /> : formatDate(startDateActivity, 'dd-MM-yyyy')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </ProtectByFeatures>
              </Table>
            </Column>
          )}

          {!error && isForeign && (
            <Column flexBasis="60%" flexGrow={2}>
              <Table accessibility={{ label: 'Información del proveedor' }}>
                <TableRow>
                  <TableCell padding="0 0 8px">
                    <Typography variant="body2">Sin información disponible.</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell disableBottomBorder padding="12px 0 16px">
                    <Typography variant="subtitle">
                      No tenemos información disponible de esta empresa proveedora extranjera.
                    </Typography>
                  </TableCell>
                </TableRow>
              </Table>
            </Column>
          )}

          {error && !loading && (
            <Column flexBasis="60%" flexGrow={2}>
              <Toast
                disableBackground
                margin="-9px 0 0 -17px"
                severity="default"
                title="Lo sentimos, estamos presentando problemas con nuestro servidor."
              >
                En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos
                minutos más.
              </Toast>
            </Column>
          )}
        </Row>
      </Card>
    </Content>
  )
}

export default LegalSuitabilitySection
